import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { getCheckoutStatus, getIntakeStatus } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import AuditIntake from "@/components/AuditIntake";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // Prefill comes from the signed-in account, not from the checkout session.
  // /checkout/status is unauthenticated by necessity - the buyer arrives here
  // straight off a Stripe redirect - and the session id sits in the URL, so
  // anything that endpoint returns is readable by anyone who ever sees that id
  // in a history list, a Referer header or a pasted link. It used to return the
  // buyer's email. This account is the one that will own the audit anyway.
  const { user } = useUser();
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });
  const [intakeDone, setIntakeDone] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    getIntakeStatus(sessionId)
      .then((r) => { if (!cancelled) setIntakeDone(!!r.submitted); })
      .catch(() => { if (!cancelled) setIntakeDone(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") { setState({ status: "paid", data, attempts }); return; }
        if (data.status === "expired") { setState({ status: "expired", data, attempts }); return; }
        if (attempts >= 8) { setState({ status: "timeout", data, attempts }); return; }
        setState({ status: "polling", data, attempts });
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 8) { setState({ status: "error", data: null, attempts }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.status === "paid";
  const failed = state.status === "expired" || state.status === "error";

  return (
    <div className="min-h-screen px-6 py-24">
      <div className="max-w-3xl mx-auto">
      <div className="card-cream p-10" data-testid="checkout-success-panel">
        <div className="flex items-center gap-3 mb-8">
          {paid && <CheckCircle2 size={18} className="text-[#e88a51]" />}
          {failed && <XCircle size={18} className="text-[#e88a51]" />}
          {!paid && !failed && <Loader2 size={18} className="text-[#e88a51] animate-spin" />}
          <span className="tick">
            {paid ? "Payment received" : failed ? "Payment failed" : "Confirming payment"}
          </span>
        </div>
        <h1 className="font-serif-display text-4xl text-[#171717] mb-6 leading-tight">
          {paid && "Payment confirmed. One more step."}
          {failed && "We could not confirm your payment."}
          {!paid && !failed && "Reconciling your transaction…"}
        </h1>
        {/* The previous copy promised provisioned access and outreach "within one
            business day". Neither existed - nothing was provisioned and no
            mechanism could contact the buyer. Replaced with what actually
            happens, plus the intake that makes the audit possible. */}
        <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed mb-8">
          {paid && "Stripe has your receipt. The audit reconciles against your own P&L, so the analyst needs a few details from you before it can start — the form below is the whole of it."}
          {failed && "No charges were captured. Return to the ladder and try again, or use the retainer form to reach the analyst."}
          {!paid && !failed && "Stripe is finalizing the settlement. This usually takes a few seconds."}
        </p>
        <div className="flex items-center gap-4">
          <Link to="/" className="btn-primary" data-testid="checkout-home-link">Return to home</Link>
          {failed && <Link to="/#pricing" className="btn-secondary">Try again</Link>}
        </div>
      </div>

      {paid && intakeDone === false && (
        <AuditIntake sessionId={sessionId} prefillEmail={user?.primaryEmailAddress?.emailAddress || ""} />
      )}
      {paid && intakeDone === true && (
        <div className="card-cream p-8 mt-8" data-testid="intake-already-done">
          <div className="tick mb-3 text-[#9a3412]">Intake received</div>
          <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed">
            Your details are already logged against this purchase. Nothing further
            is needed from you right now.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
